import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { MessageSquare, Mail, RefreshCw, ChevronLeft, ChevronRight, Send } from 'lucide-react';

const STATUS_COLORS = { QUEUED: 'bg-slate-400', SENT: 'bg-green-500', FAILED: 'bg-red-500', DELIVERED: 'bg-blue-500', OPENED: 'bg-purple-500' };
const RECIPIENT_TYPES = ['PARTICIPANT', 'STAFF', 'PORTAL_USER', 'CANDIDATE', 'OTHER'];

function MessageDetailDialog({ message, type, open, onClose }) {
  if (!message) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{type === 'sms' ? 'SMS Detail' : 'Email Detail'}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">To:</span><span>{type === 'sms' ? message.toNumber : message.toEmail}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Status:</span><Badge className={STATUS_COLORS[message.status]}>{message.status}</Badge></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Type:</span><span>{message.recipientType}</span></div>
          {message.subject && <div><span className="text-muted-foreground">Subject:</span><p className="font-medium mt-1">{message.subject}</p></div>}
          <div><span className="text-muted-foreground">Body:</span><div className="mt-1 p-3 rounded bg-muted whitespace-pre-wrap max-h-48 overflow-y-auto">{type === 'sms' ? message.messageBody : message.bodyText || message.bodyHtml}</div></div>
          {message.errorMessage && <div className="text-red-500 text-xs">Error: {message.errorMessage}</div>}
          <div className="text-xs text-muted-foreground">Sent: {message.sentAt || message.createdAt}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CommunicationLog({ type }) {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [recipientFilter, setRecipientFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);

  const statuses = type === 'sms' ? ['QUEUED', 'SENT', 'FAILED', 'DELIVERED'] : ['QUEUED', 'SENT', 'FAILED', 'OPENED'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '20' });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (recipientFilter !== 'all') params.append('recipientType', recipientFilter);
      const { data } = await api.get(`/communications/${type}?${params}`);
      setMessages(data.items || []);
      setTotal(data.total || 0);
      const statsRes = await api.get(`/communications/${type}/stats/summary`);
      setStats(statsRes.data.data);
    } catch { toast.error(`Failed to load ${type} messages`); }
    finally { setLoading(false); }
  }, [type, page, statusFilter, recipientFilter]);

  useEffect(() => { load(); }, [load]);

  const handleResend = async (msg) => {
    try {
      if (type === 'sms') {
        await api.post('/communications/sms/send', { toNumber: msg.toNumber, message: msg.messageBody, recipientType: msg.recipientType, recipientId: msg.recipientId });
      } else {
        await api.post('/communications/email/send', { toEmail: msg.toEmail, subject: msg.subject, bodyHtml: msg.bodyHtml, recipientType: msg.recipientType, recipientId: msg.recipientId });
      }
      toast.success('Message resent'); load();
    } catch { toast.error('Failed to resend'); }
  };

  return (
    <div className="space-y-6" data-testid={`${type}-log-page`}>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          {type === 'sms' ? <MessageSquare className="w-6 h-6" /> : <Mail className="w-6 h-6" />}
          {type === 'sms' ? 'SMS Messages' : 'Email Messages'}
        </h1>
        <Button variant="outline" onClick={load} data-testid="refresh-btn"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold">{stats.total}</p><p className="text-xs text-muted-foreground">Total</p></CardContent></Card>
          <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-green-600">{stats.sent}</p><p className="text-xs text-muted-foreground">Sent</p></CardContent></Card>
          <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-red-600">{stats.failed}</p><p className="text-xs text-muted-foreground">Failed</p></CardContent></Card>
          <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-slate-500">{stats.queued}</p><p className="text-xs text-muted-foreground">Queued</p></CardContent></Card>
          <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-purple-600">{type === 'sms' ? stats.delivered : stats.opened}</p><p className="text-xs text-muted-foreground">{type === 'sms' ? 'Delivered' : 'Opened'}</p></CardContent></Card>
        </div>
      )}

      <div className="flex gap-3">
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={recipientFilter} onValueChange={v => { setRecipientFilter(v); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="recipient-filter"><SelectValue placeholder="Recipient Type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Types</SelectItem>{RECIPIENT_TYPES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? <p className="text-muted-foreground">Loading...</p> : (
        <div className="space-y-2">
          {messages.map(m => (
            <Card key={m.id} className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => setSelected(m)}>
              <CardContent className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Badge className={`${STATUS_COLORS[m.status]} text-white text-xs`}>{m.status}</Badge>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{type === 'sms' ? m.toNumber : m.toEmail}</p>
                    <p className="text-xs text-muted-foreground truncate">{type === 'sms' ? m.messageBody?.substring(0, 60) : m.subject}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className="text-xs">{m.recipientType}</Badge>
                  <span className="text-xs text-muted-foreground">{new Date(m.createdAt).toLocaleString()}</span>
                  {m.status === 'FAILED' && <Button size="sm" variant="ghost" data-testid={`resend-${m.id}`} onClick={e => { e.stopPropagation(); handleResend(m); }}><Send className="w-3 h-3" /></Button>}
                </div>
              </CardContent>
            </Card>
          ))}
          {messages.length === 0 && <p className="text-center text-muted-foreground py-8">No messages found</p>}
        </div>
      )}

      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">{total} total</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm py-1">Page {page}</span>
          <Button size="sm" variant="outline" disabled={messages.length < 20} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <MessageDetailDialog message={selected} type={type} open={!!selected} onClose={() => setSelected(null)} />
    </div>
  );
}

export function SMSLogPage() { return <CommunicationLog type="sms" />; }
export function EmailLogPage() { return <CommunicationLog type="email" />; }
